/**
 * Shared transport pre-fetch utility for per-leg transport selection.
 * Pre-fetches recommended transport mode and airport codes between stops
 * in the trip builder, so the user can review/override before generation.
 *
 * Platform-agnostic — works with any fetch-like function.
 */

import { lookupCityAirportCode, MULTI_AIRPORT_CITIES, reverseLookupAirportCity } from './transportConstants.js';
import { isIslandCity } from './geographyConstants.js';
import { getCityCoords, greatCircleMiles } from './travelTimeEstimates.js';

/**
 * Build a default transportToNext object for a leg.
 * @returns {Object} Default transport config
 */
export function createDefaultTransportToNext() {
  return {
    mode: null,           // 'fly'|'drive'|'train'|'ferry'|'bus'|null
    departureAirport: null,  // { code, name, alternatives? } | null
    arrivalAirport: null,    // { code, name, alternatives? } | null
    duration: null,          // string e.g. "12h 30m"
    durationMinutes: null,   // number
    isUserOverride: false,   // true when user manually changes anything
    isLoading: false,
    source: null,            // 'auto'|'user'|null
    reason: null,            // brief explanation of why this mode was chosen
  };
}

/**
 * Look up airport info for a city, including multi-airport alternatives.
 *
 * TASK A FIX: For any stop whose resolved location contains a MULTI_AIRPORT_CITIES key
 * anywhere in the string (e.g. "The Little Nell, Aspen" → matches "Aspen"), return
 * ALL airports in that city's array (not just the one from lookupCityAirportCode).
 * This ensures the trip-builder picker shows ASE, EGE AND DEN for Aspen rather than
 * only a single code.
 *
 * @param {string} cityName - City name to look up
 * @returns {Object|null} { code, name, alternatives? } or null
 */
export function lookupCityAirports(cityName) {
  if (!cityName) return null;

  // Build a set of substrings to match against MULTI_AIRPORT_CITIES keys:
  // the full input plus each comma-separated part (so "The Little Nell, Aspen"
  // → ["the little nell, aspen", "the little nell", "aspen"]).
  const fullLower = cityName.toLowerCase().trim();
  const parts = fullLower.split(',').map(p => p.trim()).filter(Boolean);
  const searchStrings = [fullLower, ...parts];

  // Case-insensitive substring match in EITHER direction — covers partial names
  // like "Aspen" ↔ "aspen, colorado, usa" and resort-name prefixes.
  let matchedMultiAirport = null;
  for (const [multiCity, airports] of Object.entries(MULTI_AIRPORT_CITIES)) {
    const keyLower = multiCity.toLowerCase();
    const hit = searchStrings.some(s =>
      s === keyLower || s.includes(keyLower) || keyLower.includes(s)
    );
    if (hit) {
      matchedMultiAirport = { key: multiCity, airports };
      break;
    }
  }

  const code = lookupCityAirportCode(cityName);

  if (matchedMultiAirport) {
    const { airports } = matchedMultiAirport;
    // Prefer the airport matching the static lookup code; otherwise take the
    // first (highest-priority) airport for this city.
    const primary = (code && airports.find(a => a.code === code)) || airports[0];
    return {
      code: primary.code,
      name: primary.name,
      alternatives: airports.filter(a => a.code !== primary.code),
      isMultiAirport: true,
    };
  }

  if (!code) return null;
  // Bug fix: previously synthesized `${cityName} Airport`, which produced
  // garbage for hotel/resort names ("Explora Patagonia Airport" instead
  // of PMC's actual name "El Tepual / Puerto Montt"). Use the reverse
  // lookup to get the airport's canonical city name when we recognize the
  // code; fall back to a code-only label for unknown codes.
  const knownCity = reverseLookupAirportCity(code);
  const name = knownCity ? `${knownCity} (${code})` : `${code} Airport`;
  return { code, name, alternatives: [], isMultiAirport: false };
}

/**
 * Pre-fetch transport recommendation for a single leg.
 *
 * @param {string} fromCity - Departure city name
 * @param {string} toCity - Arrival city name
 * @param {Function} fetchFn - Async function that calls /api/quick-transport
 *   Signature: fetchFn(fromCity, toCity) => { mode, duration, durationMinutes, reason }
 * @returns {Object} transportToNext config
 */
export async function prefetchTransportForLeg(fromCity, toCity, fetchFn) {
  if (!fromCity || !toCity) return createDefaultTransportToNext();

  const result = createDefaultTransportToNext();
  result.isLoading = true;

  try {
    // Call quick-transport API
    const transport = await fetchFn(fromCity, toCity);

    if (transport) {
      // Map API mode to our mode format
      const modeMap = { flight: 'fly', car: 'drive', train: 'train', ferry: 'ferry', bus: 'bus' };
      result.mode = modeMap[transport.mode] || transport.mode || 'fly';
      result.duration = transport.duration || null;
      result.durationMinutes = transport.durationMinutes || null;
      result.reason = transport.reason || null;
    }
  } catch (err) {
    // Fallback: use island detection and static lookup
    const fromIsland = isIslandCity(fromCity);
    const toIsland = isIslandCity(toCity);
    result.mode = (fromIsland || toIsland) ? 'fly' : 'fly'; // Default to fly
    result.reason = 'Auto-detected (API unavailable)';
  }

  // If flying, look up airports
  if (result.mode === 'fly') {
    result.departureAirport = lookupCityAirports(fromCity);
    result.arrivalAirport = lookupCityAirports(toCity);
  }

  result.isLoading = false;
  result.source = 'auto';
  return result;
}

/**
 * Pre-fetch transport for all legs in a trip.
 *
 * @param {Array} stops - Array of stop objects with .name field
 * @param {string} departureCity - Home/departure city
 * @param {Function} fetchFn - Async fetch function for quick-transport API
 * @returns {Array} Array of transportToNext objects (one per stop, last is for return)
 */
export async function prefetchAllLegsTransport(stops, departureCity, fetchFn) {
  if (!stops || stops.length === 0) return [];

  const legs = [];

  // Leg 0: Departure city → first stop
  const firstStop = stops[0]?.resolvedLocation || stops[0]?.name || '';
  if (departureCity && firstStop) {
    legs.push(prefetchTransportForLeg(departureCity, firstStop, fetchFn));
  }

  // Legs 1..N-1: Stop i → Stop i+1
  for (let i = 0; i < stops.length - 1; i++) {
    const from = stops[i]?.resolvedLocation || stops[i]?.name || '';
    const to = stops[i + 1]?.resolvedLocation || stops[i + 1]?.name || '';
    legs.push(prefetchTransportForLeg(from, to, fetchFn));
  }

  // Last leg: Last stop → departure city (return)
  const lastStop = stops[stops.length - 1]?.resolvedLocation || stops[stops.length - 1]?.name || '';
  if (lastStop && departureCity) {
    legs.push(prefetchTransportForLeg(lastStop, departureCity, fetchFn));
  }

  return Promise.all(legs);
}

/**
 * Get display info for a transport mode.
 * @param {string} mode
 * @returns {{ emoji: string, label: string }}
 */
export function getTransportModeDisplay(mode) {
  const displays = {
    fly: { emoji: '✈️', label: 'Fly', icon: 'Plane' },
    drive: { emoji: '🚗', label: 'Drive', icon: 'Car' },
    train: { emoji: '🚆', label: 'Train', icon: 'TrainFront' },
    ferry: { emoji: '⛴️', label: 'Ferry', icon: 'Sailboat' },
    bus: { emoji: '🚌', label: 'Bus', icon: 'Bus' },
    auto: { emoji: '✨', label: 'Auto', icon: 'Sparkles' },
  };
  return displays[mode] || { emoji: '🚗', label: mode || 'Unknown', icon: 'Car' };
}

/**
 * TASK D: Find alternate airports within a drive radius from a city.
 * Used by the trip-builder "Drive further for more routes" picker so
 * a user stuck with a tiny regional airport (e.g. TSM from Taos) can
 * opt to drive to a bigger hub (e.g. ABQ or DEN) for more flight options.
 *
 * Pulls from the MULTI_AIRPORT_CITIES map (which covers the major
 * US + intl hubs) and keeps only airports whose associated city has
 * coordinates within `maxMiles` (great-circle) of the origin city AND
 * is NOT already the city's own entry. Returns sorted by distance.
 *
 * Output shape (one per alternate airport):
 *   { code, name, city, crowMiles, estDriveHours }
 *
 * If the origin city has no coords in CITY_COORDS, returns []. Not a
 * bug — we err on "don't show the option" rather than listing every
 * airport in the world. Add coords to travelTimeEstimates.js:CITY_COORDS
 * for any city you want this picker to light up.
 *
 * @param {string} cityName - Origin city (the one we'd drive from)
 * @param {Object} [opts]
 * @param {number} [opts.maxMiles=300] - Radius in crow-fly miles
 * @param {number} [opts.roadDetour=1.3] - Crow→road multiplier for drive estimate
 * @param {number} [opts.avgMph=55] - Average speed for drive hour estimate
 * @returns {Array<{code:string,name:string,city:string,crowMiles:number,estDriveHours:number}>}
 */
export function findAirportsWithinDriveRadius(cityName, opts = {}) {
  const { maxMiles = 300, roadDetour = 1.3, avgMph = 55 } = opts;
  const origin = getCityCoords(cityName);
  if (!origin) return [];

  const originLower = String(cityName || '').toLowerCase().split(',')[0].trim();
  const results = [];

  for (const [multiCity, airports] of Object.entries(MULTI_AIRPORT_CITIES)) {
    const cityCoords = getCityCoords(multiCity);
    if (!cityCoords) continue;
    const cityLower = multiCity.toLowerCase();
    if (cityLower === originLower) continue; // Skip the city's own airports
    const miles = greatCircleMiles(origin, cityCoords);
    if (!miles || miles > maxMiles) continue;
    const estDriveHours = (miles * roadDetour) / avgMph;
    for (const ap of airports) {
      results.push({
        code: ap.code,
        name: ap.name,
        city: multiCity,
        crowMiles: miles,
        estDriveHours,
      });
    }
  }

  results.sort((a, b) => a.crowMiles - b.crowMiles);
  return results;
}

/**
 * Dynamic version of findAirportsWithinDriveRadius that queries the Supabase
 * `airports_within_radius` RPC (backed by 4,500 OurAirports rows) instead of
 * the curated MULTI_AIRPORT_CITIES list (~20 cities).
 *
 * The synchronous function above is preserved for callers that can't await
 * (render-time UI). This async variant is strictly better for new code:
 *   - Works for ANY city with coords (Newcastle NTL, Asheville AVL, etc.)
 *   - Surfaces both large_airport and medium_airport in the results
 *   - Respects the same `maxMiles` + drive-hour semantics
 *
 * Fails gracefully: if the RPC errors or coords aren't available, returns
 * the synchronous curated result so existing UI never goes blank.
 *
 * @param {string} cityName - Origin city
 * @param {Object} opts
 * @param {number} [opts.maxMiles=300]
 * @param {number} [opts.roadDetour=1.3]
 * @param {number} [opts.avgMph=55]
 * @param {Object} opts.supabase - Initialized @supabase/supabase-js client
 * @returns {Promise<Array<{code:string,name:string,city:string,crowMiles:number,estDriveHours:number,country?:string}>>}
 */
export async function findAirportsWithinRadiusDynamic(cityName, opts = {}) {
  const { maxMiles = 300, roadDetour = 1.3, avgMph = 55, supabase } = opts;
  const origin = getCityCoords(cityName);
  if (!origin || !supabase?.rpc) {
    // Curated fallback: can't do dynamic without coords or client
    return findAirportsWithinDriveRadius(cityName, { maxMiles, roadDetour, avgMph });
  }

  try {
    const { data, error } = await supabase.rpc('airports_within_radius', {
      origin_lat: origin.lat,
      origin_lng: origin.lon,
      max_miles: maxMiles,
    });
    if (error) {
      // Log once for ops visibility, then fall back to curated
      // eslint-disable-next-line no-console
      console.warn('[airports_within_radius] RPC error, falling back to curated:', error.message);
      return findAirportsWithinDriveRadius(cityName, { maxMiles, roadDetour, avgMph });
    }
    if (!Array.isArray(data) || data.length === 0) {
      return findAirportsWithinDriveRadius(cityName, { maxMiles, roadDetour, avgMph });
    }

    // Filter out the city's own airports (same municipality as origin).
    // Crude lowercase match — OurAirports municipality is "Osaka" for KIX/ITM
    // but the origin might be "Osaka, Japan" — strip commas first.
    const originCityLower = String(cityName || '').toLowerCase().split(',')[0].trim();

    const mapped = data
      .filter((row) => (row.municipality || '').toLowerCase().trim() !== originCityLower)
      .map((row) => ({
        code: row.iata_code,
        name: row.name,
        city: row.municipality || '',
        country: row.country_code || '',
        crowMiles: Number(row.crow_miles) || 0,
        estDriveHours: ((Number(row.crow_miles) || 0) * roadDetour) / avgMph,
        // Flag large-hub vs regional so UIs can style / sort appropriately
        isLargeHub: row.type === 'large_airport',
      }));

    // Prefer large hubs first within the same distance band; then sort by distance
    mapped.sort((a, b) => {
      if (a.isLargeHub !== b.isLargeHub) return a.isLargeHub ? -1 : 1;
      return a.crowMiles - b.crowMiles;
    });
    return mapped;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[airports_within_radius] Unexpected error, falling back to curated:', err?.message);
    return findAirportsWithinDriveRadius(cityName, { maxMiles, roadDetour, avgMph });
  }
}

/** All supported transport modes for the picker. */
export const TRANSPORT_MODES = [
  { id: 'fly', emoji: '✈️', label: 'Fly', icon: 'Plane' },
  { id: 'drive', emoji: '🚗', label: 'Drive', icon: 'Car' },
  { id: 'train', emoji: '🚆', label: 'Train', icon: 'TrainFront' },
  { id: 'ferry', emoji: '⛴️', label: 'Ferry', icon: 'Sailboat' },
  { id: 'bus', emoji: '🚌', label: 'Bus', icon: 'Bus' },
];
